import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Events } from '../../helpers/events';
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";

import { log } from '../../helpers/log';

// ionic
import { ModalController } from '@ionic/angular';

// rxjs
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";

// components
import { AttendeeDetailComponent } from '../attendee-detail/attendee-detail.component';
import { NewsfeedDetailComponent } from '../newsfeed-detail/newsfeed-detail.component';

// providers
import { EventService } from "../../services/event.service";
import { UserService } from "../../services/user.service";
import { OverlayService } from "../../services/overlay.service";
import { PlatformService } from "../../services/platform.service";
import { ParticipantService } from "../../services/participant.service";
import { RouterExtService } from "../../services/router-ext.service";
import { MarketplaceService } from "../../services/marketplace.service";
import { NewsfeedService } from "../../services/newsfeed.service";

// models
import { Event } from "../../models/event";
import { Feed } from "../../models/feed";
import { Participant } from "../../models/participant";
import { User } from "../../models/user";
import { Newsfeed } from "../../models/newsfeed";

@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
})
export class NotificationsComponent implements OnInit, OnDestroy {
    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * current event
     *
     * @type {User}
     */
    public user: User;

    /**
     * event
     *
     * @type {Event}
     */
    @Input() event: Event;

    /**
     * is component in modal?
     *
     * @type {boolean}
     */
    @Input() isModal: boolean = true;

    public loading: boolean = false;

    /**
     * selected language
     *
     * @type string
     */
    public userLang: string;

    /**
     * blocks feed detail to be opened multiple times
     *
     * @type boolean
     */
    public feedDetailModalOpened: boolean = false;

    /**
     * constructor
     *
     * @param pushPageService
     * @param userProvider
     * @param appEvents
     */
    constructor(
        private router: Router,
        public appEvents: Events,
        public modalController: ModalController,
        public plt: PlatformService,
        public eventService: EventService,
        public userService: UserService,
        public overlayService: OverlayService,
        public participantService: ParticipantService,
        public translate: TranslateService,
        public routerExtService: RouterExtService,
        public marketplaceService: MarketplaceService,
        public newsfeedService: NewsfeedService
    ) {
        this.userLang = this.translate.currentLang;
    }

    ngOnInit() {
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe(
            (user: User) => {
                this.user = user;

                // load all newsfeed
                this.participantService.getFeeds(this.user.selected_participant_id).subscribe(
                    (feeds: Feed[]) => {
                        this.plt.setDashboardFeeds(feeds);
                    },
                    (error) => {
                        log('error', 'getFeeds', error);
                    });
            });
    }

    /**
     * show details about feed notification
     *
     * @param {Feed} feed
     */
    public openFeedDetail(feed: Feed): void {
        switch (feed.data.type) {
            case 'like':
            case 'visit':
                this.participantService.getById(+feed.data.participant_id).subscribe(
                    (participant) => {
                        this.showAttendeeDetail(participant);
                    });
                break;
            case 'message':
                //this.router.navigate([this.plt.defaultLink + '/messages', feed.data.participant_id]);
                break;
            case 'newsfeed-comment':
            case 'newsfeed-like':
            case 'newsfeed-confirm':
            case 'newsfeed-reject':
                this.showNewsfeedDetail(feed.data.newsfeed_id);
                break;
            case 'marketplace-post-waiting':
            case 'marketplace-post-created':
            case 'marketplace-post-comment':
            case 'marketplace-post-like':
            case 'marketplace-post-confirm':
            case 'marketplace-post-reject':
            case 'marketplace-post-category-deleted':
            case 'marketplace-post-visit':
            case 'marketplace-post-contact-removed':
            case 'marketplace-post-contact-added':
                this.showMarketplacePostDetail(feed.data.post_id, feed.data.marketplace_id);
                break;
        }
    }

    /**
     * go to participants detail page
     *
     * @param participant
     *
     * @return void
     */
    public async showAttendeeDetail(attendee: Participant, force: boolean = false, appointmentRequest = false) {
        if ((!this.user.canProfileDetails(attendee) && !force) || this.feedDetailModalOpened) {
            return false;
        }

        this.feedDetailModalOpened = true;

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AttendeeDetailComponent,
            cssClass: 'attendee-modal',
            componentProps: {
                attendee: attendee,
                appointmentRequest: appointmentRequest
            },
            id: 'attendee-detail'
        });

        modal.onWillDismiss().then((data) => {

            this.feedDetailModalOpened = false;

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    /**
     * open newsfeed detail with comments in new modal
     *
     * @return modal
     */
    public async openNewsfeedDetail(newsfeed: Newsfeed) {

        this.routerExtService.softNavigate();

        newsfeed.commentsVisible = true;

        const detail = await this.modalController.create({
            component: NewsfeedDetailComponent,
            cssClass: 'newsfeed-detail',
            componentProps: {
                newsfeed: newsfeed,
                event: this.event,
                modal: true
            }
        });

        detail.onWillDismiss().then((data) => {
            this.feedDetailModalOpened = false;
            if (data.data) {
                if (data.data.keywords) {
                    // TODO[jg] refresh details about comments and so...
                }
                if (data.data?.action == 'close' || data.role == 'backdrop') {
                    this.routerExtService.softBack();
                }

                // delete event from opened modal
                if (data.data.deleteId) {
                    // remove this notification....
                }
            }
        });

        return await detail.present();
    }

    /**
     * load newsfeed detail and open modal if possible
     *
     * @return modal
     */
    public async showNewsfeedDetail(newsfeedId: number) {
        if (!this.feedDetailModalOpened) {
            this.feedDetailModalOpened = true;
            this.newsfeedService.getById(newsfeedId).subscribe(
                (newsfeed: Newsfeed) => {
                    this.openNewsfeedDetail(new Newsfeed(newsfeed));
                },
                (error) => {
                    this.feedDetailModalOpened = false;
                });
        }
    }

    /**
     * open marketplace post detail with comments in new modal
     *
     * @return modal
     */
    public async showMarketplacePostDetail(postId: number, marketplaceId: number) {
        // update url
        let link = this.plt.defaultLink + '/home/marketplace/list?post_id=' + postId;
        // add event id also
        link += '&marketplace_id=' + marketplaceId;
        link += '&event_id=' + this.event.id.toString();

        this.router.navigateByUrl(link);
    }

    /**
     * rediret to chat with selected attendee
     *
     */
    public goToMessages(attendeeId: number) {
        // check if participant can start conversation
        this.participantService.canStartConversation(attendeeId).subscribe(
            (result) => {
                // participant can join conversation
                if (result) {
                    if (this.isModal) {
                        this.modalController.dismiss();
                    }
                    this.router.navigate([this.plt.defaultLink + '/messages/direct/', attendeeId]);
                }
                // participant can not join conversation
                else {
                    this.overlayService.showError(this.translate.instant('CONVERSATION_LIMIT_REACHED'));
                }
            }
        );
    }


    close() {
        this.modalController.dismiss({ action: 'close' });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
